import React, { useState } from 'react';
import { X, Plus, Save, Eye, AlertCircle } from 'lucide-react';
import { useUpdateLayout } from '../../../services/queries/layoutQueries';
import { useDragAndDrop } from '../../../hooks/useDragAndDrop';
import { FieldPalette } from './FieldPalette';
import { LayoutSection } from './LayoutSection';
import { LayoutPreview } from './LayoutPreview';
import type { PageLayout, PageLayoutSection } from '../../../services/queries/layoutQueries';
import type { CustomField } from '../../../services/queries/fieldQueries';
import { useToast } from '../../../contexts/ToastContext';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface LayoutBuilderProps {
  layout: PageLayout;
  objectName: string;
  allFields: CustomField[];
  onClose: () => void;
}

export const LayoutBuilder: React.FC<LayoutBuilderProps> = ({
  layout: initialLayout,
  objectName,
  allFields,
  onClose
}) => {
  const [layout, setLayout] = useState<PageLayout>(initialLayout);
  const [showPreview, setShowPreview] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  const updateLayoutMutation = useUpdateLayout();
  const { draggedItem, isDragging, handleDragStart, handleDragEnd } = useDragAndDrop();
  const { success, error } = useToast();

  // Drag-and-drop sensors for section reordering
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Require 5px movement to activate drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get fields that are not yet in the layout
  const usedFieldIds = new Set(
    layout.sections.flatMap(section => section.fields.map(f => f.fieldId))
  );
  const availableFields = allFields.filter(field => !usedFieldIds.has(field.id));

  const handleFieldDragStart = (field: CustomField) => {
    handleDragStart({
      id: field.id,
      type: 'field',
      data: field
    });
  };

  const handleFieldDrop = (sectionId: string, column: 0 | 1, index: number, transferData?: any) => {
    // Use draggedItem from React state, or fall back to dataTransfer payload
    const field: CustomField | undefined = (draggedItem?.type === 'field' ? draggedItem.data : undefined)
      || (transferData ? allFields.find(f => f.id === transferData.id) || transferData : undefined);
    const section = layout.sections.find(s => s.id === sectionId);
    if (!field) return;

    const newField = {
      id: `lf_${Date.now()}`,
      fieldId: field.id,
      fieldName: field.fieldName,
      fieldLabel: field.fieldLabel,
      order: section ? section.fields.length + 1 : 1,
      column: (column + 1) as 1 | 2,
      required: field.required,
      readonly: false
    };

    // Update local state immediately
    setLayout(prev => ({
      ...prev,
      sections: prev.sections.map(s => {
        if (s.id === sectionId) {
          const newFields = [...s.fields];
          const insertIndex = s.columns === 2
            ? (column * Math.ceil(s.fields.length / 2)) + index
            : index;
          newFields.splice(insertIndex, 0, newField);
          return { ...s, fields: newFields };
        }
        return s;
      })
    }));

    setHasChanges(true);
  };

  const handleRemoveField = (sectionId: string, fieldId: string) => {
    // Update local state immediately
    setLayout(prev => ({
      ...prev,
      sections: prev.sections.map(s => {
        if (s.id === sectionId) {
          return { ...s, fields: s.fields.filter(f => f.fieldId !== fieldId) };
        }
        return s;
      })
    }));

    setHasChanges(true);
  };

  const handleAddSection = () => {
    const sectionLabel = `New Section ${layout.sections.length + 1}`;
    const sectionOrder = layout.sections.length + 1;

    const newSection = {
      id: `section_${Date.now()}`,
      label: sectionLabel,
      order: sectionOrder,
      columns: 2 as 1 | 2,
      collapsible: true,
      fields: []
    };

    setLayout(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
    setHasChanges(true);
  };

  const handleEditSection = (_sectionId: string) => {
    // Inline editing is handled via handleSectionLabelChange; pencil/label click opens inline input
  };

  const handleSectionLabelChange = (sectionId: string, newLabel: string) => {
    setLayout(prev => ({
      ...prev,
      sections: prev.sections.map(s =>
        s.id === sectionId ? { ...s, label: newLabel } : s
      )
    }));
    setHasChanges(true);
  };

  const handleDeleteSection = (sectionId: string) => {
    if (!window.confirm('Are you sure you want to delete this section? Fields will be returned to the palette.')) {
      return;
    }

    // Remove from local state immediately
    setLayout(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.id !== sectionId)
    }));

    setHasChanges(true);
    success('Section removed from draft', 'Click Save Changes to persist.');
  };

  const handleToggleColumns = (sectionId: string) => {
    setLayout(prev => ({
      ...prev,
      sections: prev.sections.map(s =>
        s.id === sectionId ? { ...s, columns: (s.columns === 1 ? 2 : 1) as 1 | 2 } : s
      )
    }));
    setHasChanges(true);
  };

  const handleFieldReorder = (sectionId: string, oldIndex: number, newIndex: number) => {
    setLayout(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id === sectionId) {
          const reorderedFields = arrayMove(section.fields, oldIndex, newIndex);

          // Update order for all fields
          const fieldsWithUpdatedOrder = reorderedFields.map((field, index) => ({
            ...field,
            order: index + 1
          }));

          return {
            ...section,
            fields: fieldsWithUpdatedOrder
          };
        }
        return section;
      })
    }));
    setHasChanges(true);
  };

  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setLayout(prev => {
      const oldIndex = prev.sections.findIndex(s => s.id === active.id);
      const newIndex = prev.sections.findIndex(s => s.id === over.id);

      const reorderedSections = arrayMove(prev.sections, oldIndex, newIndex);

      // Update order field for all sections
      const sectionsWithUpdatedOrder = reorderedSections.map((section, index) => ({
        ...section,
        order: index + 1
      }));

      return {
        ...prev,
        sections: sectionsWithUpdatedOrder
      };
    });

    setHasChanges(true);
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateLayoutMutation.mutateAsync({
        layoutId: layout.id,
        updates: {
          sections: layout.sections
        }
      });
      success('Layout saved!', 'Changes are live in the application.');
      setHasChanges(false);
      // Close the builder after successful save so it reloads fresh
      setTimeout(() => onClose(), 500);
    } catch (err: any) {
      error('Save failed', err.message || 'Please try again');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleCloseX = () => {
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full h-full max-w-[98vw] max-h-[98vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Visual Layout Builder</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {layout.name} - {objectName}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {hasChanges && (
              <div className="flex items-center gap-2 text-sm text-yellow-600 dark:text-yellow-400">
                <AlertCircle className="w-4 h-4" />
                Pending changes — click Save
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className={`px-3 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                showPreview
                  ? 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <Eye className="w-4 h-4" />
              Preview
            </button>

            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={!hasChanges || isSaving || updateLayoutMutation.isPending}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {isSaving || updateLayoutMutation.isPending ? 'Syncing...' : hasChanges ? 'Save Changes' : 'Saved'}
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCloseX();
              }}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Field Palette */}
          <FieldPalette
            availableFields={availableFields}
            onDragStart={handleFieldDragStart}
            onDragEnd={handleDragEnd}
          />

          {/* Layout Canvas */}
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
            <div className="max-w-5xl mx-auto space-y-4">
              {/* Add Section Button */}
              <button
                onClick={handleAddSection}
                className="w-full p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-purple-400 dark:hover:border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-colors flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400"
              >
                <Plus className="w-5 h-5" />
                Add New Section
              </button>

              {/* Layout Sections with Drag-and-Drop */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleSectionDragEnd}
              >
                <SortableContext
                  items={layout.sections.map(s => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {layout.sections.map(section => (
                    <SortableSection
                      key={section.id}
                      section={section}
                      onDrop={handleFieldDrop}
                      onRemoveField={handleRemoveField}
                      onEditSection={handleEditSection}
                      onSectionLabelChange={handleSectionLabelChange}
                      onDeleteSection={handleDeleteSection}
                      onToggleColumns={handleToggleColumns}
                      onFieldReorder={handleFieldReorder}
                      isDragging={isDragging}
                    />
                  ))}
                </SortableContext>
              </DndContext>

              {layout.sections.length === 0 && (
                <div className="text-center py-12 text-gray-400 dark:text-gray-500">
                  <p className="text-lg font-medium mb-2">No sections yet</p>
                  <p className="text-sm">Click "Add New Section" to get started</p>
                </div>
              )}
            </div>
          </div>

          {/* Preview Panel */}
          {showPreview && (
            <LayoutPreview
              layout={layout}
              objectName={objectName}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Sortable Section Wrapper Component
interface SortableSectionProps {
  section: PageLayoutSection;
  onDrop: (sectionId: string, column: 0 | 1, index: number) => void;
  onRemoveField: (sectionId: string, fieldId: string) => void;
  onEditSection: (sectionId: string) => void;
  onSectionLabelChange: (sectionId: string, newLabel: string) => void;
  onDeleteSection: (sectionId: string) => void;
  onToggleColumns: (sectionId: string) => void;
  onFieldReorder?: (sectionId: string, oldIndex: number, newIndex: number) => void;
  isDragging: boolean;
}

const SortableSection: React.FC<SortableSectionProps> = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSectionDragging,
  } = useSortable({ id: props.section.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSectionDragging ? 0.5 : 1,
    zIndex: isSectionDragging ? 999 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style} className="mb-4">
      <div className={isSectionDragging ? 'ring-2 ring-purple-500 rounded-lg shadow-xl' : ''}>
        <LayoutSection
          {...props}
          onDragOver={() => {}}
          dragHandleProps={{ ...attributes, ...listeners }}
        />
      </div>
    </div>
  );
};
